import { Dayjs } from 'dayjs'
import { SetOptional } from 'type-fest'
import { z } from 'zod'

import { add, day, diff, getDaysBetween, getNextWeekdays, getToday, isSame, isSameOrAfter, isSameOrBefore } from '~/astra'
import type { Schemas } from '~/clients/api-client'
import { Include, Location } from '~/storm'

import { formatToIsoString, getActiveAndFutureProfiles } from '../../Form/utils'

type ApiServicePeriod = Schemas['ServicePeriod']

const ISO_WEEKDAYS = ['1', '2', '3', '4', '5', '6', '7'] as const

const DEFAULT_OPEN = '08:00'
const DEFAULT_CLOSE = '15:00'
const DEFAULT_OPEN_CLOSE = { open: DEFAULT_OPEN, close: DEFAULT_CLOSE, isClosed: false }
const CLOSED_VALUE = { open: DEFAULT_OPEN, close: DEFAULT_CLOSE, isClosed: true }

export const defaultOpeningHours: OpeningHoursValue = [
    DEFAULT_OPEN_CLOSE,
    DEFAULT_OPEN_CLOSE,
    DEFAULT_OPEN_CLOSE,
    DEFAULT_OPEN_CLOSE,
    DEFAULT_OPEN_CLOSE,
    CLOSED_VALUE,
    CLOSED_VALUE,
]

const dayOpeningHoursSchema = z.object({
    open: z.string(),
    close: z.string(),
    isClosed: z.boolean(),
})

export function getDistinctIsoWeekdaysInRange(startDate: Dayjs | undefined, endDate: Dayjs | undefined): Set<string> {
    if (startDate && endDate && isSameOrBefore(startDate, endDate)) {
        return new Set(getDaysBetween(startDate, endDate).map(d => String(d.isoWeekday())))
    }
    return new Set(ISO_WEEKDAYS)
}

export type DayOpeningHours = z.infer<typeof dayOpeningHoursSchema>
export type OpeningHoursValue = DayOpeningHours[]

const isValidOpeningHour = (hours: DayOpeningHours) => {
    if (hours.isClosed) return true
    if (!hours.close && !hours.open) return true
    return !!hours.close && !!hours.open && hours.close > hours.open
}

const validateServicePeriodOpeningHours = (sp: { openingHours: DayOpeningHours[]; startDate?: Dayjs; endDate?: Dayjs }, ctx: z.RefinementCtx) => {
    const relevant = getDistinctIsoWeekdaysInRange(sp.startDate, sp.endDate)
    sp.openingHours.forEach((hours, i) => {
        const iso = ISO_WEEKDAYS[i]
        if (!iso || !relevant.has(iso)) return
        if (!isValidOpeningHour(hours)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Stengetid må være etter åpningstid',
                path: ['openingHours', i],
            })
        }
    })
}

const servicePeriodSchema = z
    .object({
        openingHours: z.array(dayOpeningHoursSchema).length(7),
        lunchDuration: z.string().optional(),
        changeDuration: z.string().optional(),
        startDate: z.custom<Dayjs>().optional(),
        endDate: z.custom<Dayjs>().optional(),
        originalServicePeriod: z.custom<ApiServicePeriod | undefined>().optional(),
        // used internally to indicate that this is a newly added service period
        isNewlyAdded: z.boolean().optional(),
    })
    .superRefine(validateServicePeriodOpeningHours)

const profileSurgeryTypeGroupSchema = z.object({
    id: z.number().optional(),
    surgeryTypeGroup: z.object({
        id: z.number(),
        code: z.string(),
    }),
})

export type FormProfileSurgeryTypeGroup = z.infer<typeof profileSurgeryTypeGroupSchema>

export type FormServicePeriod = z.infer<typeof servicePeriodSchema>

type LocationStaffingConfig = Schemas['LocationStaffingConfig']

/**
 * Expert-only solver config overridden per location profile (LocationStaffingConfig).
 * Optional as a whole; when present, max must be >= min.
 */
export const locationStaffingConfigSchema = z
    .object({
        required_surgeons_min: z.number({ invalid_type_error: 'Påkrevd felt' }).int('Må være et heltall').min(0, 'Kan ikke være negativ'),
        required_surgeons_max: z.number({ invalid_type_error: 'Påkrevd felt' }).int('Må være et heltall').min(0, 'Kan ikke være negativ'),
        room_type: z.enum(['O', 'D']).nullable(),
    })
    .superRefine((data, ctx) => {
        if (data.required_surgeons_max < data.required_surgeons_min) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Maks må være større eller lik min', path: ['required_surgeons_max'] })
        }
    })

export type FormLocationStaffingConfig = z.infer<typeof locationStaffingConfigSchema>

export const DEFAULT_LOCATION_STAFFING_CONFIG: FormLocationStaffingConfig = {
    required_surgeons_min: 1,
    required_surgeons_max: 1,
    room_type: null,
}

const toFormLocationStaffingConfig = (config: LocationStaffingConfig | null | undefined): FormLocationStaffingConfig | undefined => {
    if (!config) return undefined
    return {
        required_surgeons_min: config.required_surgeons.min,
        required_surgeons_max: config.required_surgeons.max,
        room_type: config.room_type ?? null,
    }
}

export const toApiLocationStaffingConfig = (config: FormLocationStaffingConfig | undefined | null): LocationStaffingConfig | undefined | null => {
    if (!config) return config
    return {
        required_surgeons: { min: config.required_surgeons_min, max: config.required_surgeons_max },
        room_type: config.room_type,
    }
}

const profileSchema = z.object({
    id: z.number().optional(),
    activeFrom: z.custom<Dayjs>().optional(),
    surgeryTypeGroups: z.array(profileSurgeryTypeGroupSchema),
    extraStaffingConfig: locationStaffingConfigSchema.optional().nullable(),
})

export type FormLocationProfile = z.infer<typeof profileSchema>

const validateFutureProfiles = (data: FormLocationProfile[], ctx: z.RefinementCtx) => {
    let previousActiveFrom: Dayjs | undefined
    data.forEach((p, index) => {
        if (!p.activeFrom) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Påkrevd felt', path: [index, 'activeFrom'] })
            return
        }
        if (previousActiveFrom && isSameOrBefore(p.activeFrom, previousActiveFrom)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Startdato må være etter at forrige periode begynner', path: [index, 'activeFrom'] })
        }
        previousActiveFrom = p.activeFrom
        if (isSameOrBefore(p.activeFrom, getToday())) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Aktiv fra må være etter i dag', path: [index, 'activeFrom'] })
        }
    })
}

const validateServicePeriods = (data: FormServicePeriod[], ctx: z.RefinementCtx) => {
    let previousEndDate: Dayjs | undefined
    data.forEach((sp, index) => {
        if (sp.startDate && sp.endDate && isSameOrBefore(sp.endDate, sp.startDate)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Startdato må være før slutt dato', path: [index, 'startDate'] })
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Sluttdato må være etter startdato', path: [index, 'endDate'] })
        }
        if (previousEndDate) {
            if (sp.startDate && isSameOrBefore(sp.startDate, previousEndDate)) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Må starte etter at forrige periode slutter', path: [index, 'startDate'] })
            }
            // check for "continuous" service periods - no gaps between periods
            const nextDay = add(previousEndDate, 1, 'day')
            const nextWeekday = getNextWeekdays(nextDay, 1)[0]
            if (sp.startDate && !isSame(nextDay, sp.startDate) && !isSame(nextWeekday, sp.startDate)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Må starte dagen etter forrige periode',
                    path: [index, 'startDate'],
                })
            }
        }
        previousEndDate = sp.endDate
    })
}

export const locationFormSchema = z.object({
    roomCode: z.string().min(1, 'Stuenavn er påkrevd'),
    dipsId: z.string(),
    defaultCareLevel: z.enum(['inpatient', 'outpatient'], { required_error: 'Omsorgsnivå er påkrevd' }),
    departments: z.array(z.number()).min(1, 'Minst en avdeling er påkrevd'),
    activeProfile: profileSchema,
    futureProfiles: z.array(profileSchema).superRefine(validateFutureProfiles),
    servicePeriods: z.array(servicePeriodSchema).superRefine(validateServicePeriods),
})

export type LocationFormParsedValues = z.infer<typeof locationFormSchema>
export type LocationFormDefaultValues = SetOptional<LocationFormParsedValues, 'defaultCareLevel'>

export const defaultProfile: FormLocationProfile = {
    id: undefined,
    activeFrom: undefined,
    surgeryTypeGroups: [],
    extraStaffingConfig: undefined,
}

export const defaultServicePeriod = {
    openingHours: defaultOpeningHours,
    lunchDuration: undefined,
    changeDuration: undefined,
    startDate: undefined,
    endDate: undefined,
    originalServicePeriod: undefined,
    isNewlyAdded: false,
}
export const defaultFormValues: LocationFormDefaultValues = {
    roomCode: '',
    dipsId: '',
    defaultCareLevel: undefined,
    departments: [],
    activeProfile: defaultProfile,
    futureProfiles: [],
    servicePeriods: [defaultServicePeriod],
}

/**
 * Converts location service periods to an array of opening hours, with an element per day of the week.
 * ApiServicePeriods can group the same opening hours for multiple days within one element.
 * This function "explodes" the service periods into a list of opening hours for each day of the week.
 */
function servicePeriodsToOpeningHours(servicePeriods: ApiServicePeriod[]): OpeningHoursValue {
    const result: OpeningHoursValue = defaultOpeningHours.map(d => ({ ...d }))
    for (const period of servicePeriods) {
        for (const dayStr of period.days_of_week) {
            const weekdayIndex = ISO_WEEKDAYS.indexOf(dayStr as (typeof ISO_WEEKDAYS)[number])
            if (weekdayIndex !== -1) {
                result[weekdayIndex] = { open: period.open, close: period.close, isClosed: period.is_closed }
            }
        }
    }
    return result
}

const servicePeriodSorter = (a: FormServicePeriod, b: FormServicePeriod) => {
    if (!a.startDate && !b.startDate) {
        if (!a.endDate && !b.endDate) return 0
        if (!a.endDate) return -1
        if (!b.endDate) return 1
        return diff(a.endDate, b.endDate)
    }
    if (!a.startDate) return -1
    if (!b.startDate) return 1
    return diff(a.startDate, b.startDate)
}

/**
 * Groups service periods by (start_date, end_date).
 */
function apiServicePeriodsToFormServicePeriods(servicePeriods: ApiServicePeriod[]): FormServicePeriod[] {
    const byPeriod = new Map<string, ApiServicePeriod[]>()
    for (const sp of servicePeriods) {
        const key = `${sp.start_date}-${sp.end_date}`
        const list = byPeriod.get(key) ?? []
        list.push(sp)
        byPeriod.set(key, list)
    }
    const formServicePeriods = Array.from(byPeriod.values())
        .map(periods => {
            const openingHours = servicePeriodsToOpeningHours(periods)
            // Using last defined service-period for the current period to get the lunch and change durations
            // note that this might overwrite the durations of the other periods when converting back to api payloads.
            // This is because we group service-periods by period (start-date and end-date), and the durations are the same for all periods with the same period.
            const restData = periods.at(-1)
            return {
                openingHours,
                lunchDuration: restData?.lunch_duration ?? undefined,
                changeDuration: restData?.change_duration ?? undefined,
                startDate: restData?.start_date ? day(restData.start_date) : undefined,
                endDate: restData?.end_date ? day(restData.end_date) : undefined,
                originalServicePeriod: restData,
                isNewlyAdded: false,
            }
        })
        .filter(sp => !sp.endDate || sp.isNewlyAdded || isSameOrAfter(sp.endDate, getToday()))
        .toSorted(servicePeriodSorter)
    return formServicePeriods
}

type LocationForFormState = Include<
    Location,
    { locationProfiles: { locationSurgeryTypeGroups: { surgeryTypeGroup: true } }; departments: true; dipsResource: true }
>

const toFormProfile = (profile: LocationForFormState['locationProfiles'][number]): FormLocationProfile => ({
    id: profile.id,
    activeFrom: profile.active_from ? day(profile.active_from) : undefined,
    surgeryTypeGroups: profile.locationSurgeryTypeGroups.flatMap(s => (s.surgeryTypeGroup ? [{ id: s.id, surgeryTypeGroup: s.surgeryTypeGroup }] : [])),
    extraStaffingConfig: toFormLocationStaffingConfig(profile.extra_staffing_config),
})

export function locationToFormState(location: LocationForFormState): LocationFormDefaultValues {
    const formProfiles = location.locationProfiles.map(toFormProfile)
    const { active: activeFormProfile, future: futureFormProfiles } = getActiveAndFutureProfiles(formProfiles)

    const servicePeriods = apiServicePeriodsToFormServicePeriods(location.service_periods ?? [])
    const formServicePeriods = servicePeriods.length > 0 ? servicePeriods : [defaultServicePeriod]
    return {
        roomCode: location.room_code,
        dipsId: location.dipsResource?.id !== undefined ? String(location.dipsResource.id) : '',
        defaultCareLevel: location.default_care_level,
        departments: location.departments.map(d => d.id),
        activeProfile: activeFormProfile ?? defaultProfile,
        futureProfiles: futureFormProfiles,
        servicePeriods: formServicePeriods,
    }
}

type PartialApiServicePeriod = Pick<ApiServicePeriod, 'open' | 'close' | 'days_of_week' | 'is_closed'>
/*
 * The API structure of servicePeriods can group the same service-period to multiple days.
 * eg. Grouping opening hours for monday and tuesday into a single service-period.
 * In the formState we have one value for each weekday - as this maps better to the UI.
 * This function groups the opening hours by the same service-period (same open and close times).
 */
function openingHoursToGroupedApiServicePeriods(servicePeriod: FormServicePeriod): PartialApiServicePeriod[] {
    const byHours = new Map<string, PartialApiServicePeriod>()
    const openTimes = servicePeriod.openingHours
    // no need to define days that do not fall within the service period
    const relevantIsoWeekdays = getDistinctIsoWeekdaysInRange(servicePeriod.startDate, servicePeriod.endDate)

    openTimes.forEach((day, i) => {
        const isoWeekday = ISO_WEEKDAYS[i]
        if (!isoWeekday || !relevantIsoWeekdays.has(isoWeekday)) {
            return
        }
        const key = `${day.open}-${day.close}-${day.isClosed ? 'closed' : 'open'}`
        const existing = byHours.get(key) ?? { open: day.open, close: day.close, is_closed: day.isClosed, days_of_week: [] }
        existing.days_of_week.push(isoWeekday)
        byHours.set(key, existing)
    })
    return Array.from(byHours.values()).map(sp => ({ ...sp, days_of_week: sp.days_of_week.toSorted() }))
}

export function formServicePeriodsToApiServicePeriods(servicePeriods: FormServicePeriod[]): ApiServicePeriod[] {
    return servicePeriods.toSorted(servicePeriodSorter).flatMap(sp => {
        const spFromOpeningHours = openingHoursToGroupedApiServicePeriods(sp)
        return spFromOpeningHours.map(partialSp => ({
            ...(sp.originalServicePeriod ?? {}),
            ...partialSp,
            start_date: formatToIsoString(sp.startDate),
            end_date: formatToIsoString(sp.endDate),
        }))
    })
}

export type ProfileApiPayload = {
    location_id: number
    id?: number
    active_from?: string
    extra_staffing_config?: LocationStaffingConfig | null
    profileSurgeryTypeGroups: { id?: number; surgery_type_group_id: number }[]
}

export function formStateToApiPayloads(formState: LocationFormDefaultValues, locationId: number) {
    const allProfiles = [formState.activeProfile, ...formState.futureProfiles].filter(Boolean)

    const locationProfiles: ProfileApiPayload[] = allProfiles
        .map(profile => ({
            location_id: locationId,
            id: profile.id,
            active_from: formatToIsoString(profile.activeFrom),
            extra_staffing_config: toApiLocationStaffingConfig(profile.extraStaffingConfig),
            profileSurgeryTypeGroups: profile.surgeryTypeGroups.map(s => ({
                surgery_type_group_id: s.surgeryTypeGroup.id,
                id: s.id,
            })),
        }))
        .filter(p => p.id !== undefined || p.profileSurgeryTypeGroups.length > 0 || !!p.active_from || p.extra_staffing_config !== undefined)

    const servicePeriods = formServicePeriodsToApiServicePeriods(formState.servicePeriods)

    return {
        location: {
            room_code: formState.roomCode,
            default_care_level: formState.defaultCareLevel,
            servicePeriods,
        },
        locationProfiles,
        departmentAssignments: formState.departments.map(d => ({
            location_id: locationId,
            department_id: d,
        })),
    }
}
